import { computed, reactive, ref, watch } from 'vue'
import type { ChangeReasonKind, ChangeReasons } from '@/Network/Types/Requests/ChangeReasonRequests'
import {
  emptyChangeReason,
  isChangeReasonValid,
  toChangeReasonInput,
  type ChangeReasonOption,
  type ChangeReasonValue
} from '@/utils/changeReasons'

export interface ChangeReasonFieldConfig {
  kind: ChangeReasonKind
  title: string
  label: string
  options: ChangeReasonOption[]
}

const KINDS: ChangeReasonKind[] = ['opening', 'external', 'removal', 'deletion']

/**
 * Estado do motivo no modal (SPEC §7.1): os reenvios (M6, preAllocate, coringa) leem o mesmo
 * valor. `fields` é calculado pela tela a partir do que a ação vai fazer (SPEC §4.1).
 */
export function useChangeReasonForm(fields: () => ChangeReasonFieldConfig[]) {
  const values = reactive(
    Object.fromEntries(KINDS.map((kind) => [kind, emptyChangeReason()])) as Record<
      ChangeReasonKind,
      ChangeReasonValue
    >
  )
  const showErrors = ref(false)
  const activeFields = computed(fields)

  // Review Focus 2: trocar time/substituto pode tirar a opção escolhida; ela não pode ir no payload.
  watch(
    activeFields,
    (configs) => {
      for (const { kind, options } of configs) {
        const code = values[kind].code
        if (code && !options.some((option) => option.value === code)) {
          values[kind] = emptyChangeReason()
        }
      }
    },
    { flush: 'sync', immediate: true }
  )

  function reset(): void {
    for (const kind of KINDS) values[kind] = emptyChangeReason()
    showErrors.value = false
  }

  function validate(): boolean {
    const valid = activeFields.value.every(({ kind, options }) =>
      isChangeReasonValid(values[kind], options)
    )
    showErrors.value = !valid
    return valid
  }

  function build(extra: { originStationId?: number | null } = {}): ChangeReasons | undefined {
    if (activeFields.value.length === 0) return undefined
    const reasons: ChangeReasons = {}
    for (const { kind, options } of activeFields.value) {
      const input = toChangeReasonInput(values[kind], options)
      if (input) reasons[kind] = input
    }
    if (reasons.removal && extra.originStationId != null) {
      reasons.originStationId = extra.originStationId
    }
    return reasons
  }

  return { values, showErrors, fields: activeFields, reset, validate, build }
}
